import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ShoppingCart, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { brand } from '../brand';

// Variant-aware branding (see App.jsx). The standalone Chip Bank build
// shows Chip Bank wording on this shared login screen. The casino name is
// driven by the active brand (VITE_BRAND) so reskins update it automatically.
const APP_VARIANT = import.meta.env.VITE_APP_VARIANT || 'admin';
const BRAND_NAME = (brand && brand.naming && brand.naming.appName) || 'EDH Casino';
const PLAYER_BANK = (brand && brand.naming && brand.naming.chipBank) || 'Player Bank';
const BRAND =
  APP_VARIANT === 'chipbank'
    ? {
        title: `${BRAND_NAME} ${PLAYER_BANK}`,
        subtitle: `Sign in to access the ${PLAYER_BANK}`,
        footer: `Only authorized staff can access the ${PLAYER_BANK}`,
      }
    : APP_VARIANT === 'pos'
    ? {
        title: `${BRAND_NAME} POS`,
        subtitle: 'Sign in to the register',
        footer: 'Authorized staff only \u2014 point of sale',
      }
    : {
        title: `${BRAND_NAME} Admin`,
        subtitle: 'Sign in to access the admin panel',
        footer: 'Only authorized administrators can access this panel',
      };

// POS gets a register icon; everything else keeps the shield.
const BrandIcon = APP_VARIANT === 'pos' ? ShoppingCart : Shield;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gray-900 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-2xl mb-4">
            <BrandIcon className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-2xl font-bold text-white">{BRAND.title}</h1>
          <p className="text-gray-400 mt-1">{BRAND.subtitle}</p>
        </div>

        {/* Login form */}
        <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white 
                         placeholder-gray-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                placeholder="admin@edhcasino.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white 
                           placeholder-gray-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          {BRAND.footer}
        </p>
      </div>
    </div>
  );
}
